import { App, Button, Form, Input, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import ModalLayout from '../../layouts/ModalLayout';
import { UserService } from '../../services/userApiService';
import { getApiErrorCode, getApiErrorDetail } from '../../utils/apiErrorCode';
import {
  getTransportsFromRegistration,
  passkeyRegisterWithOptionsString,
  registrationResponseToAttestationJson,
} from '../../utils/webauthnClient';

// Ant Design forms and modal, WebAuthn helpers, local service
const userService = new UserService();

// Form ID used for linking Ant Design 'submit' button with form
const PASSKEY_CREATE_FORM_ID = 'passkey-create-form';

function passkeyApiNameToErrorsTranslationKey(
  apiName: string | undefined,
):
  | 'errors.passKeyDataRequired'
  | 'errors.passKeyForbidden'
  | 'errors.passKeyLimitReached'
  | 'errors.passKeyLockedOut'
  | 'errors.passKeyNotAllowed'
  | 'errors.passKeyNotEnabled'
  | 'errors.passKeyNotFound'
  | 'errors.passKeySessionExpired'
  | 'errors.passKeyVerificationFailed'
  | null {
  if (!apiName) {
    return null;
  }
  switch (apiName) {
    case ApiErrorEnum.PasskeyDataRequired:
      return 'errors.passKeyDataRequired';
    case ApiErrorEnum.Forbidden:
      return 'errors.passKeyForbidden';
    case ApiErrorEnum.PasskeyLimitReached:
      return 'errors.passKeyLimitReached';
    case ApiErrorEnum.PasskeyLockedOut:
      return 'errors.passKeyLockedOut';
    case ApiErrorEnum.PasskeyNotAllowed:
      return 'errors.passKeyNotAllowed';
    case ApiErrorEnum.PasskeyNotEnabled:
      return 'errors.passKeyNotEnabled';
    case ApiErrorEnum.PasskeyNotFound:
      return 'errors.passKeyNotFound';
    case ApiErrorEnum.PasskeySessionExpired:
      return 'errors.passKeySessionExpired';
    case ApiErrorEnum.PasskeyVerificationFailed:
      return 'errors.passKeyVerificationFailed';
    default:
      return null;
  }
}

/** User-visible registration error (i18n); empty when caller should treat separately (cancel). */
function getPasskeyRegistrationErrorMessage(error: unknown, t: TFunction): string {
  const domError = error as { name?: string };
  if (domError.name === 'InvalidStateError') {
    return t('modal.passkeyCreateForm.registerErrorDeviceAlreadyRegistered');
  }
  if (domError.name === 'NotSupportedError') {
    return t('modal.passkeyCreateForm.registerErrorNotSupported');
  }

  const apiName = getApiErrorCode(error);
  const detailText = getApiErrorDetail(error);
  const passkeyKey = passkeyApiNameToErrorsTranslationKey(apiName);
  if (passkeyKey) {
    return t(passkeyKey);
  }

  if (detailText?.trim()) {
    return detailText.trim();
  }

  return t('modal.passkeyCreateForm.registerErrorGeneric');
}

// Props for the PasskeyCreateForm component
export interface PasskeyCreateFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

// Values handled by the form instance
interface PasskeyCreateFormValues {
  deviceName: string;
}

/**
 * PasskeyCreateForm
 *
 * Modal form component to register a new passkey device (WebAuthn).
 * Handles the full registration ceremony, device label entry, and error/reporting UX.
 */
export default function PasskeyCreateForm({ onClose, onSuccess }: PasskeyCreateFormProps) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();

  // Ant Design form instance for controlled validation and reset
  const [form] = Form.useForm<PasskeyCreateFormValues>();

  // Indicates registration is in progress (UI disables button/inputs)
  const [registerBusy, setRegisterBusy] = useState(false);

  /**
   * Validation rules for the deviceName field.
   * - Must be 1-100 non-whitespace characters.
   * - Translated error messaging.
   */
  const deviceNameRules = useMemo(
    () => [
      {
        validator: async (_: unknown, value: string | undefined) => {
          const trimmed = (value ?? '').trim();
          if (trimmed.length < 1 || trimmed.length > 100) {
            // Use translation for invalid device name feedback
            return Promise.reject(new Error(t('modal.passkeyCreateForm.deviceNameInvalid')));
          }
          return Promise.resolve();
        },
      },
    ],
    [t],
  );

  /**
   * Handles the actual passkey creation flow:
   * 1. Get registration options from backend.
   * 2. Run WebAuthn ceremony in-browser.
   * 3. Submit attestation to backend with user label.
   * 4. Handle errors and notify user with relevant UX.
   */
  const handleFinish = useCallback(
    async (values: PasskeyCreateFormValues) => {
      const trimmed = values.deviceName.trim();
      setRegisterBusy(true);
      try {
        // Request passkey registration initiation from the backend
        const { options, sessionId } = await userService.passkeyRegisterOptions();

        // Run browser-side WebAuthn registration ceremony
        const registration = await passkeyRegisterWithOptionsString(options);

        // Normalize attestation and relevant transports for backend
        const attestationResponse = registrationResponseToAttestationJson(registration);
        const transports = getTransportsFromRegistration(registration);

        // Complete passkey registration (send all data server-side)
        await userService.passkeyRegisterComplete({
          attestationResponse,
          deviceName: trimmed,
          sessionId,
          transports,
        });

        // UX: success notification
        message.success(t('modal.passkeyCreateForm.registerSuccess'));
        form.resetFields();
        onSuccess?.();
      } catch (error: unknown) {
        const webAuthnDomError = error as { name?: string };
        if (
          webAuthnDomError?.name === 'NotAllowedError' ||
          webAuthnDomError?.name === 'AbortError'
        ) {
          notification.info({ title: t('modal.passkeyCreateForm.passkeyCancelled') });
        } else {
          notification.error({
            title: getPasskeyRegistrationErrorMessage(error, t),
          });
        }
      } finally {
        setRegisterBusy(false);
      }
    },
    [form, message, notification, onSuccess, t],
  );

  return (
    <ModalLayout>
      <ModalLayout.Content>
        <div className="space-y-3 pt-2">
          {/* Description (above input), helps guide the user */}
          <Typography.Paragraph className="text-sm">
            {t('modal.passkeyCreateForm.addDescription')}
          </Typography.Paragraph>
          <Form<PasskeyCreateFormValues>
            id={PASSKEY_CREATE_FORM_ID}
            form={form}
            initialValues={{ deviceName: '' }}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => void handleFinish(values)}
          >
            <Form.Item name="deviceName" rules={deviceNameRules}>
              <Input
                maxLength={100}
                placeholder={t('modal.passkeyCreateForm.deviceNamePlaceholder')}
                showCount
              />
            </Form.Item>
          </Form>
        </div>
      </ModalLayout.Content>
      <ModalLayout.Footer>
        <div className="flex justify-end gap-2">
          {/* Cancel closes modal */}
          <Button htmlType="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* Submit button tied to form, shows loading spinner */}
          <Button
            form={PASSKEY_CREATE_FORM_ID}
            htmlType="submit"
            loading={registerBusy}
            type="primary"
          >
            {t('modal.passkeyCreateForm.continueAuthenticator')}
          </Button>
        </div>
      </ModalLayout.Footer>
    </ModalLayout>
  );
}
